import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import PasswordInput from '../ui/password-input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import api from '../../services/api';

const EditUserDialog = ({ isOpen, onClose, user, onUserUpdated }) => {
  // Accept both shapes: direct {id, handle, ...} and wrapper {user: {...}, stats, ...}
  const userData = user?.user || user;
  const propBalance = user?.stats?.credits_balance;

  const [formData, setFormData] = useState({
    handle: '',
    email: '',
    roles: [],
    is_active: true
  });
  const [loading, setLoading] = useState(false);
  const [balance, setBalance] = useState(null);
  const [creditDelta, setCreditDelta] = useState('');
  const [creditReason, setCreditReason] = useState('admin_adjust');
  const [adjusting, setAdjusting] = useState(false);
  const [newUserPw, setNewUserPw] = useState('');
  const [settingPw, setSettingPw] = useState(false);

  useEffect(() => {
    if (userData && userData.id && isOpen) {
      setFormData({
        handle: userData.handle || '',
        email: userData.email || '',
        roles: Array.isArray(userData.roles) ? userData.roles : [],
        is_active: userData.is_active !== false
      });
      setCreditDelta('');
      setCreditReason('admin_adjust');
      setNewUserPw('');
      // Prefer the balance from the wrapper shape; otherwise fetch the
      // user's full record so the dialog can display + adjust credits
      // even when the caller passed only the sanitized direct shape.
      if (typeof propBalance === 'number') {
        setBalance(propBalance);
      } else {
        setBalance(null);
        api.admin.getUser(userData.id)
          .then((r) => setBalance(r.data?.stats?.credits_balance ?? null))
          .catch(() => setBalance(null));
      }
    }
  }, [userData, isOpen, propBalance]);

  const handleAdjust = async () => {
    const amount = parseFloat(creditDelta);
    if (!Number.isFinite(amount) || amount === 0) {
      toast.error('Bitte einen Betrag ungleich 0 eingeben.');
      return;
    }
    setAdjusting(true);
    try {
      const res = await api.admin.updateUserCredits(userData.id, {
        amount,
        reason: creditReason,
      });
      const newBalance = res.data?.new_balance;
      if (typeof newBalance === 'number') setBalance(newBalance);
      toast.success(
        `Saldo angepasst (${amount > 0 ? '+' : ''}${amount} VC). Neuer Saldo: ${newBalance ?? '?'}`
      );
      setCreditDelta('');
      if (onUserUpdated) onUserUpdated();
    } catch (error) {
      toast.error(
        'Saldo-Anpassung fehlgeschlagen: ' +
          (error.response?.data?.detail || error.message)
      );
    } finally {
      setAdjusting(false);
    }
  };

  const handleSetPassword = async () => {
    if (newUserPw.length < 8) {
      toast.error('Passwort muss mindestens 8 Zeichen haben.');
      return;
    }
    setSettingPw(true);
    try {
      await api.admin.resetUserPassword(userData.id, { new_password: newUserPw });
      toast.success('Passwort gesetzt. Der User muss sich neu einloggen.');
      setNewUserPw('');
    } catch (error) {
      toast.error(
        'Passwort setzen fehlgeschlagen: ' +
          (error.response?.data?.detail || error.message)
      );
    } finally {
      setSettingPw(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userData || !userData.id) {
      toast.error('User ID is missing');
      return;
    }

    setLoading(true);

    try {
      const response = await api.admin.updateUser(userData.id, formData);
      
      if (response.data.success) {
        toast.success('User updated successfully!');
        if (onUserUpdated) {
          onUserUpdated();
        }
        onClose();
      } else {
        toast.error('Update failed');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = (role, checked) => {
    setFormData(prev => ({
      ...prev,
      roles: checked
        ? [...prev.roles.filter(r => r !== role), role]
        : prev.roles.filter(r => r !== role)
    }));
  };

  // When isOpen is false, render nothing (also prevents an orphan dialog
  // from sticking around when the caller forgot to clear `user`).
  if (!isOpen) return null;

  // Defensive: caller passed isOpen=true but a malformed user object.
  // Render a closable dialog instead of crashing.
  if (!userData || !userData.id) {
    return (
      <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>User data is unavailable.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Button onClick={onClose} className="mt-4">Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit User: {userData.handle || 'Unknown'}</DialogTitle>
          <DialogDescription>
            Update user information and permissions
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="handle">Username</Label>
            <Input
              id="handle"
              value={formData.handle}
              onChange={(e) => setFormData(prev => ({ ...prev, handle: e.target.value }))}
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              required
            />
          </div>
          
          <div className="space-y-2">
            <Label>Roles</Label>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="role_user"
                  checked={formData.roles.includes('user')}
                  onChange={(e) => handleRoleChange('user', e.target.checked)}
                />
                <Label htmlFor="role_user">User</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="role_creator"
                  checked={formData.roles.includes('creator')}
                  onChange={(e) => handleRoleChange('creator', e.target.checked)}
                />
                <Label htmlFor="role_creator">Creator</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="role_admin"
                  checked={formData.roles.includes('admin')}
                  onChange={(e) => handleRoleChange('admin', e.target.checked)}
                />
                <Label htmlFor="role_admin">Admin</Label>
              </div>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
            />
            <Label htmlFor="is_active">Account Active</Label>
          </div>

          <div className="flex gap-4 pt-4">
            <Button type="submit" disabled={loading} className="flex-1">
              {loading ? 'Updating...' : 'Update User'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
          </div>
        </form>

        <div className="mt-6 pt-4 border-t space-y-3">
          <div className="flex items-baseline justify-between">
            <Label className="text-base">Credits anpassen</Label>
            <span className="text-sm text-gray-600">
              Aktueller Saldo:{' '}
              <span className="font-semibold">
                {balance === null ? '…' : `${balance} VC`}
              </span>
            </span>
          </div>
          <div className="flex gap-2">
            <Input
              type="number"
              step="0.01"
              placeholder="±Betrag"
              value={creditDelta}
              onChange={(e) => setCreditDelta(e.target.value)}
              className="flex-1"
            />
            <select
              value={creditReason}
              onChange={(e) => setCreditReason(e.target.value)}
              className="px-3 py-2 border rounded-md text-sm bg-white"
            >
              <option value="admin_adjust">admin_adjust</option>
              <option value="bonus">bonus</option>
              <option value="refund">refund</option>
              <option value="correction">correction</option>
            </select>
            <Button
              type="button"
              onClick={handleAdjust}
              disabled={adjusting || !creditDelta}
              variant="outline"
            >
              {adjusting ? '…' : 'Anpassen'}
            </Button>
          </div>
          <p className="text-xs text-gray-500">
            Positiver Wert = gutschreiben, negativer = abziehen. Backend lehnt
            ab, wenn der Saldo dadurch negativ würde.
          </p>
        </div>

        <div className="mt-6 pt-4 border-t space-y-3">
          <Label className="text-base">Passwort setzen</Label>
          <div className="flex gap-2">
            <PasswordInput
              placeholder="Neues Passwort (min. 8 Zeichen)"
              value={newUserPw}
              onChange={(e) => setNewUserPw(e.target.value)}
              className="flex-1"
              autoComplete="new-password"
            />
            <Button
              type="button"
              onClick={handleSetPassword}
              disabled={settingPw || newUserPw.length < 8}
              variant="outline"
            >
              {settingPw ? '…' : 'Setzen'}
            </Button>
          </div>
          <p className="text-xs text-gray-500">
            Setzt das Passwort des Users direkt. Bestehende Sitzungen werden
            ungültig — der User muss sich mit dem neuen Passwort neu einloggen.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default EditUserDialog;